import { Component } from '@angular/core';
import { Product } from '../product';
import { CommonModule, NgTemplateOutlet } from '@angular/common';
import { Navbar } from '../../components/navbar/navbar';
import { RouterModule } from '@angular/router';
import { MatTabsModule } from '@angular/material/tabs';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import { AuthService } from '../../auth/auth.service';
import { ApplicationPolicyListService } from '../../services/application-policy-list.service';

@Component({
  selector: 'app-product-list',
  imports: [
    CommonModule,
    NgTemplateOutlet,
    Navbar,
    RouterModule,
    MatTabsModule,
    MatProgressSpinnerModule,
    MatFormFieldModule,
    MatSelectModule,
    TranslateModule
  ],
  templateUrl: './product-list.html',
  styleUrl: './product-list.scss',
})
export class ProductList {
  constructor(
    private productService: Product,
    private authService: AuthService,
    private appListService: ApplicationPolicyListService,
  ) {}
  products: any[] = [];
  productsPlan: any[] = [];
  nameIcon: string = '';

  activeTab = '';

  productId: any;
  productsAllPlan: any = [];

  setTab(tab: string) {
    if (tab == '') {
      console.log('tab', tab);
      this.activeTab;
    }

    this.activeTab = tab;
  }

  currentIndex = 0;
  intervalId: any;

  planId: any[] = [];
  productsOptions: any[] = [];
  amountOption: any[] = [];

  planName: any;

  result: any;
  allProdId: any;
  loading: boolean = true;

  /** Best-sellers: plans the logged-in agent has sold most, from their own apps + policies */
  popularPlanIds = new Set<string>();
  private soldCount = new Map<string, number>();

  /** Top coverage-per-kip plans get a "best value" badge (computed after load) */
  bestValueIds = new Set<string>();

  ngOnInit(): void {
    this.productService.findAllProducts().subscribe((res: any) => {
      this.products = res;
    });

    this.productService.findAllProductsPlan().subscribe((res: any) => {
      this.productsAllPlan = res;
      this.computeBestValue();
      this.loading = false;
    });

    this.loadPopularity();
  }

  /** "ຂາຍດີ" = how often THIS agent has sold each plan. Backend scopes to the
   *  logged-in agent via the JWT, so we just pull all their rows and tally by plan. */
  private loadPopularity(): void {
    const decoded = this.authService.decodeToken() as any;
    const userType = decoded?.UserType || '';
    // PageSize must be > 0 — the service drops PageNumber/PageSize when falsy, so 0 would paginate.
    this.appListService
      .applicationPolicyList({ UserType: userType, PageNumber: 1, PageSize: 100000 })
      .subscribe({
        next: (res: any) => this.computePopularity(res?.dataList || []),
        error: () => { /* popularity is an enhancement — the list still works without it */ },
      });
  }

  private computePopularity(rows: any[]): void {
    const counts = new Map<string, number>();
    for (const r of rows) {
      const id = r?.planId;
      if (!id) continue;
      counts.set(id, (counts.get(id) || 0) + 1);
    }
    this.soldCount = counts;
    this.popularPlanIds = new Set(
      [...counts.entries()]
        .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
        .slice(0, 2)
        .map(([id]) => id)
    );
  }

  isPopular(planId: string): boolean {
    return this.popularPlanIds.has(planId);
  }

  private soldCountOf(planId: string): number {
    return this.soldCount.get(planId) || 0;
  }

  private computeBestValue(): void {
    const ranked = (this.productsAllPlan || [])
      .filter((x: any) => x.premiumPerYear > 0 && x.maxCoverageAmt > 0)
      .map((x: any) => ({ id: x.planId, leverage: x.maxCoverageAmt / x.premiumPerYear }))
      .sort((a: any, b: any) => b.leverage - a.leverage)
      .slice(0, 2);
    this.bestValueIds = new Set(ranked.map((r: any) => r.id));
  }

  /** Coverage-per-kip: higher = more cover for the money ("ສຸດຄຸ້ມ") */
  private leverage(x: any): number {
    return x?.premiumPerYear > 0 && x?.maxCoverageAmt > 0 ? x.maxCoverageAmt / x.premiumPerYear : 0;
  }

  /** Surfaced at the very top of the All-view so the agent sees the strongest picks first.
   *  It is exactly the union of the two highlight signals — best-sellers (🔥 ຂາຍດີ) AND
   *  best-value (★ ຄຸ້ມຄ່າ) — so BOTH badges are guaranteed to appear in the strip, not just
   *  whichever ranks higher. Best-sellers lead, best-value follows (highlightRank order). */
  get featuredPlans(): any[] {
    const ids = new Set<string>([...this.popularPlanIds, ...this.bestValueIds]);
    return (this.productsAllPlan || [])
      .filter((x: any) => ids.has(x.planId))
      .sort((a: any, b: any) => this.highlightRank(b) - this.highlightRank(a));
  }

  /** Highlight order within a family: best-seller first, then best-value, then natural tier order */
  private highlightRank(plan: any): number {
    if (this.isPopular(plan?.planId)) return 2;
    if (this.bestValueIds.has(plan?.planId)) return 1;
    return 0;
  }

  /** Cap the entrance-stagger delay so long lists don't animate in forever */
  staggerDelay(i: number): number {
    return Math.min(i, 8) * 55;
  }

  /** Family icon per product id */
  private readonly ICONS: Record<string, string> = {
    WL: 'fa-shield-heart',
    RT: 'fa-umbrella-beach',
    ST: 'fa-bolt',
    SV: 'fa-piggy-bank',
    LC: 'fa-hand-holding-heart',
  };

  productIcon(productId: string): string {
    return this.ICONS[productId] || 'fa-file-shield';
  }

  /** All-view: plans grouped by product family; best-value plan floated to the front of each family */
  get productGroups(): { productId: string; name: string; plans: any[] }[] {
    return (this.products || []).map((p: any) => ({
      productId: p.productId,
      name: p.nameLa,
      plans: (this.productsAllPlan || [])
        .filter((x: any) => x.productId === p.productId)
        // stable sort: best-seller then best-value leads, the rest keep their natural tier order
        .sort((a: any, b: any) => this.highlightRank(b) - this.highlightRank(a))
    }));
  }

  /** Tab view: plans of the selected product only */
  get filteredPlans(): any[] {
    return (this.productsAllPlan || []).filter((x: any) => x.productId === this.activeTab);
  }

  /** "ມື້ລະ X" framing — a year of premium feels huge, a day of premium feels easy */
  perDay(premiumPerYear: number): number {
    if (!premiumPerYear) return 0;
    return Math.ceil(premiumPerYear / 365 / 100) * 100; // round up to nearest 100 kip
  }

  onImgError(event: Event): void {
    (event.target as HTMLImageElement).src = 'assets/images/insurance-package.png';
  }
}
